/**
 * Piezas compartidas de las tarjetas de estadísticas: el estado de carga de cada tarjeta, el
 * chip de datos simulados, el aviso de «conectá la cuenta» y el tooltip de los gráficos.
 *
 * Cada tarjeta carga y falla por su cuenta: un 502 de Twilio pone «No pudimos traer…» con
 * «Reintentar» SÓLO en las tarjetas de Twilio, nunca voltea la página entera.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, PlugZap, RefreshCw } from "lucide-react";
import { esSinNumero, textoAvisoConectar } from "../../logic/estadisticas";

export const RUTA_CONFIG = "/activa/oficial/configuracion";

/** Lo que falta para mostrar el contenido: cargando (esqueleto) o error (con Reintentar). */
export interface EstadoPedido { cargando: boolean; error: string }

export function Esqueleto({ alto = "h-40", etiqueta = "Cargando" }: { alto?: string; etiqueta?: string }) {
  return <div className={`${alto} rounded-xl bg-slate-100 animate-pulse`} aria-busy="true" aria-label={etiqueta} />;
}

export function ErrorTarjeta({ texto, onReintentar }: { texto: string; onReintentar?: () => void }) {
  return (
    <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
      <p className="flex items-start gap-2"><AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> <span>{texto}</span></p>
      {onReintentar && (
        <button type="button" onClick={onReintentar} className="mt-2 inline-flex items-center gap-1.5 font-semibold hover:underline">
          <RefreshCw size={13} /> Reintentar
        </button>
      )}
    </div>
  );
}

/**
 * Envoltorio del contenido de una tarjeta: si todavía no hay dato, esqueleto o error; si hay, el
 * contenido (y si el último refresco falló, lo último que se vio más una línea que lo dice).
 */
export function ContenidoTarjeta<D>({ dato, estado, onReintentar, alto, queFalta = "estos datos", children }: {
  dato: D | null; estado: EstadoPedido; onReintentar: () => void; alto?: string; queFalta?: string; children: (d: D) => ReactNode;
}) {
  if (dato === null) {
    if (estado.error) return <ErrorTarjeta texto={`No pudimos traer ${queFalta}. ${estado.error}`} onReintentar={onReintentar} />;
    return <Esqueleto alto={alto} />;
  }
  return (
    <>
      {estado.error && (
        <p className="mb-3 text-xs text-amber-700">
          No pudimos actualizar: {estado.error} Lo que ves es lo último que trajimos.{" "}
          <button type="button" onClick={onReintentar} className="font-semibold underline">Reintentar</button>
        </p>
      )}
      {children(dato)}
    </>
  );
}

export function ChipSimulado({ texto = "Datos simulados" }: { texto?: string }) {
  return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{texto}</span>;
}

/**
 * «Conectá la cuenta de Twilio para ver estos datos» con enlace a Configuración (y el motivo, si vino).
 * Con el motivo `sin_numero` (credenciales reales andando, falta el número) dice eso: pedir «Conectá
 * la cuenta» con la cuenta ya conectada mandaba a rehacer algo que estaba bien. Ahí el motivo no se
 * repite abajo (es el código o el mismo texto).
 */
export function AvisoConectar({ motivo }: { motivo?: string }) {
  const sinNumero = esSinNumero(motivo);
  return (
    <div className="rounded-xl border-2 border-dashed border-slate-200 p-5 text-center">
      <PlugZap size={18} className="mx-auto text-slate-400" />
      <p className="mt-2 text-sm text-slate-600">{textoAvisoConectar(motivo)}</p>
      {motivo && !sinNumero && <p className="mt-1 text-xs text-slate-400">{motivo}</p>}
      <Link to={`${RUTA_CONFIG}?seccion=cuenta`} className="mt-3 inline-block text-sm font-semibold text-violet-700 hover:underline">Ir a Configuración</Link>
    </div>
  );
}

/** Estado vacío honesto: texto centrado en una caja punteada. */
export function Vacio({ children }: { children: ReactNode }) {
  return <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">{children}</div>;
}

/** Estilo común de los tooltips de recharts: tarjeta blanca, texto en tinta (nunca en el color de la serie). */
export const ESTILO_TOOLTIP = {
  contentStyle: { borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(15,23,42,.08)", fontSize: 12, color: "#0f172a" },
  labelStyle: { color: "#475569", fontWeight: 600 },
  itemStyle: { color: "#0f172a" },
} as const;

/** Leyenda propia (en HTML): el color va en la marca, el texto en tinta. */
export function Leyenda({ items }: { items: { color: string; label: string; linea?: boolean }[] }) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-600" aria-label="Referencias">
      {items.map((i) => (
        <li key={i.label} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={i.linea ? "inline-block w-4 h-0.5 rounded" : "inline-block w-2.5 h-2.5 rounded-sm"} style={{ backgroundColor: i.color }} />
          {i.label}
        </li>
      ))}
    </ul>
  );
}
